import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Box, Toolbar } from "@mui/material";
import jwtDecode from "jwt-decode";
import { images } from "./util/images";
import { UserProvider } from "./components/auth/auth-context";
import CustomerHeader from "./components/headers/customerHeader";
import BusinessHeader from "./components/headers/businessHeader";
import RouteGroups from "./routeGroups";
import BackgroundWrapper from "./util/backGroundWrapper";
import { useDispatch } from "react-redux";
import { setSelectedBusiness } from "./redux/actions/businessActions";
import FloatingCompanyBrand from "./components/common/floatingCompanyBrand";
import Footer from "./components/common/footer";
import i18n from "./util/i18n";
import { getToken, setToken as persistToken } from "./util/authUtils";
import { useIsMobile } from "./util/utils";

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const currentPath = location.pathname;

  // -----------------------------
  // 🧠 Token + Auth Management - START
  // -----------------------------

  const [token, setTokenState] = useState(getToken());
  const [fnToExecute, setFnToExecute] = useState(null);

  // Memoized decoded token: Only re-decode if the raw token string changes.
  const memoizedDecodedToken = useMemo(() => {
    try {
      return token ? jwtDecode(token) : null;
    } catch (e) {
      console.error("Error decoding token:", e);
      return null;
    }
  }, [token]);

  // Provider user state: The actual user data provided through context.
  const [providerUser, setProviderUser] = useState(() => memoizedDecodedToken || {});

  // --- Login & Logout Callbacks (DEFINED FIRST) ---
  // These are defined early so they can be used by useEffects below.
  const login = useCallback(
    async (access_token) => {
      persistToken(access_token);
      setTokenState(access_token);

      if (fnToExecute) {
        await fnToExecute();
        sessionStorage.removeItem("redirectAfterLogin");
      } else {
        const redirectPath = sessionStorage.getItem("redirectAfterLogin") || "/";
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath, { replace: true });
      }
    },
    [fnToExecute, navigate]
  );

  const logout = useCallback(async () => {
    try {
      persistToken(null);
      localStorage.clear();
      sessionStorage.clear();
      setTokenState(null); // Clears token, which triggers memoizedDecodedToken and providerUser reset
      if (fnToExecute) await fnToExecute();
      setFnToExecute(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  }, [fnToExecute]);

  const setFnToExecuteAfterLoginSuccess = useCallback((cb) => {
    setFnToExecute(() => cb);
  }, []);

  const updateProviderUserContext = useCallback((newUserData) => {
    setProviderUser((prev) => {
      if (
        prev.userId === newUserData.userId &&
        prev.defaultBusinessId === newUserData.defaultBusinessId &&
        prev.staffId === newUserData.staffId
      ) {
        return prev;
      }
      return { ...prev, ...newUserData };
    });
  }, []);

  // --- Effects (Now `logout` is defined) ---

  // Effect to sync providerUser from memoizedDecodedToken
  useEffect(() => {
    if (memoizedDecodedToken) {
      setProviderUser((prev) => {
        if (
          prev.userId === memoizedDecodedToken.userId &&
          prev.defaultBusinessId === memoizedDecodedToken.defaultBusinessId &&
          prev.staffId === memoizedDecodedToken.staffId
        ) {
          return prev;
        }
        return memoizedDecodedToken;
      });
    } else {
      if (Object.keys(providerUser).length > 0) {
        setProviderUser({});
      }
    }
  }, [memoizedDecodedToken]);

  // Set selected business in Redux when defaultBusinessId changes in the memoized decoded token.
  useEffect(() => {
    if (memoizedDecodedToken?.defaultBusinessId) {
      dispatch(setSelectedBusiness(memoizedDecodedToken.defaultBusinessId));
    }
  }, [memoizedDecodedToken?.defaultBusinessId, dispatch]);

  // Auto logout on token expiry
  useEffect(() => {
    if (memoizedDecodedToken && memoizedDecodedToken.exp < Date.now() / 1000) {
      logout(); // This is now safe, as `logout` is defined above
      console.warn("Token expired — logged out.");
    }
  }, [memoizedDecodedToken, logout]);

  // -----------------------------
  // Language Setting
  // -----------------------------
  useEffect(() => {
    const selectedLanguage = localStorage.getItem("selectedLanguage");
    if (selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [i18n]);

  // -----------------------------
  // Routing & Rendering Components Memoization
  // -----------------------------

  const memoizedRouteGroupsElement = useMemo(() => (
    <RouteGroups
      providerUser={providerUser}
      decodedToken={memoizedDecodedToken}
      setProviderUser={setProviderUser}
      token={token}
      login={login}
      logout={logout}
      setImage={() => {}}
    />
  ), [
    providerUser,
    memoizedDecodedToken,
    setProviderUser,
    token,
    login,
    logout
  ]);

  const IsBookingRoute = useCallback(() => {
    const hardcodedPrefixes = ["/login", "/signup", "/forgotpassword", "/biz", "/about", "/privacy", "/faq", "/terms", "/embed", "/search"];
    const isHardcodedRoute = hardcodedPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    const pathSegments = location.pathname.split("/").filter(Boolean);
    return !isHardcodedRoute && pathSegments.length >= 2;
  }, [location.pathname]);

  const pagesWithBackground = useMemo(() => ["/login", "/signup", "/forgotpassword"], []);
  const nonContainerWrapper = useMemo(() => ["/", "/biz/calendar", "/biz/printview"], []);
  const isBookingRoute = IsBookingRoute();
  const isBusinessBookingRoute = useMemo(() => /^\/\d+\/[^/]+/.test(currentPath), [currentPath]);
  const isBusinessRoute = useMemo(() => currentPath.startsWith("/biz/") || ["/biz", "/about", "/privacy", "/faq", "/terms"].includes(currentPath), [currentPath]);
  const isCustomerHeaderVisible = useMemo(() => currentPath.startsWith("/category/") || (!isBusinessRoute && !pagesWithBackground.includes(currentPath) && !isBusinessBookingRoute && !isBookingRoute), [currentPath, isBusinessRoute, pagesWithBackground, isBusinessBookingRoute, isBookingRoute]);
  const isNonContainerWrapper = useMemo(() => nonContainerWrapper.includes(currentPath), [nonContainerWrapper, currentPath]);
  const isEmbedRoute = useMemo(() => currentPath.startsWith("/embed"), [currentPath]);

  const renderedBusinessRoutes = useMemo(() => (
    isNonContainerWrapper ? memoizedRouteGroupsElement : (
      <Container maxWidth="xl" sx={{ padding: 0, marginTop: isEmbedRoute ? '' : "100px" }}>
        {memoizedRouteGroupsElement}
      </Container>
    )
  ), [isNonContainerWrapper, isEmbedRoute, memoizedRouteGroupsElement]);

  const HeaderComponent = useMemo(() => {
    if (isEmbedRoute) return null;
    if (isBusinessRoute) {
      return (
        <Box sx={{ pt: { xs: '40px', sm: '75px' } }}>
          <BusinessHeader /> {isMobile && <Toolbar />}
        </Box>
      );
    }
    if (isCustomerHeaderVisible) {
      return (
        <Box sx={{ pt: { xs: '160px', sm: '75px' }, backgroundColor: "#22374a" }}>
          <CustomerHeader />
        </Box>
      );
    }
    return <FloatingCompanyBrand />;
  }, [isEmbedRoute, isBusinessRoute, isMobile, isCustomerHeaderVisible]);

  const footer = useMemo(() => isEmbedRoute ? null : <Footer />, [isEmbedRoute]);

  return (
    <>
      <BackgroundWrapper
        id="main"
        currentPath={currentPath}
        pagesWithBackground={pagesWithBackground}
        backgroundImage=""
        images={images}
      >
        <UserProvider
          value={{
            login,
            logout,
            providerUser,
            setFnToExecuteAfterLoginSuccess,
            fnToExecute: null,
            updateProviderUser: updateProviderUserContext,
          }}
        >
          {HeaderComponent}
          {renderedBusinessRoutes}
        </UserProvider>
      </BackgroundWrapper>
      {footer}
    </>
  );
}

export default App;